import { useState } from "react";
import { Link } from "react-router-dom";
import LegalPage from "../components/legal/LegalPage";
import {
  emailLimit,
  getformattedPhoneNumber,
  getPlainPhoneNumber,
  selectLoader,
  userNameLimit,
  validateEmail,
  validatePhoneNumber,
  validateTextInput,
} from "../utils/Helper";
import APIService from "../utils/APIService";
import appToast, { TOAST_TYPE } from "../utils/AppToast";

const Contactus = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    const updatedValue =
      name === "phone" ? getformattedPhoneNumber(value) : value;

    setFormData((currentData) => ({
      ...currentData,
      [name]: updatedValue,
    }));
    setErrors((currentErrors) => ({ ...currentErrors, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!validateTextInput(formData.name).isValid) {
      newErrors.name = validateTextInput(formData.name).message;
    }
    if (!validateEmail(formData.email).isValid) {
      newErrors.email = validateEmail(formData.email).message;
    }
    if (!validatePhoneNumber(formData.phone).isValid) {
      newErrors.phone = validatePhoneNumber(formData.phone).message;
    }
    if (!validateTextInput(formData.message).isValid) {
      newErrors.message = validateTextInput(formData.message).message;
    }

    return newErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setResponseMessage("");

    try {
      const response = await APIService.post("/contactus", {
        ...formData,
        phone: getPlainPhoneNumber(formData.phone),
      });

      if (response.contact) {
        appToast(response.message, TOAST_TYPE.SUCCESS);
        setFormData({ name: "", email: "", phone: "", message: "" });
      } else {
        appToast(response.message, TOAST_TYPE.ERROR);
      }
    } catch (error) {
      setResponseMessage("Error connecting to server. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <LegalPage title="Contact Us">
    <p className="contact-intro">
      Need help with HappyEnding or the Slack integration? Our team is here to
      help.
    </p>

    <h2>1. Support Contact</h2>
    <p>
      For support questions, login issues, Slack integration issues, or help
      using HappyEnding, contact us at:
    </p>
    <aside className="support-contact-card">
      <a href="mailto:support@happyending.ai">support@happyending.ai</a>
    </aside>

    <h2>2. Slack Integration Support</h2>
    <p>
      If you are having trouble connecting HappyEnding to Slack, please include
      the following details when contacting support:
    </p>
    <ul>
      <li>Your HappyEnding account email</li>
      <li>Your Slack workspace name</li>
      <li>The project name you are trying to connect</li>
      <li>The Slack channel selected during authorization</li>
      <li>A short description of the issue</li>
      <li>Any screenshot or error message, if available</li>
    </ul>

    <h2>3. Common Slack Integration Issues</h2>
    <ul>
      <li>
        If Slack authorization fails, make sure you selected a valid Slack
        workspace and channel.
      </li>
      <li>
        If Slack messages are not appearing, confirm that the project is
        connected to Slack and that the selected channel still exists.
      </li>
      <li>
        If you disconnected Slack, project activity messages will no longer be
        sent to Slack.
      </li>
      <li>
        If you are unable to log in to HappyEnding, contact support and include
        the email address used for login.
      </li>
    </ul>

    <h2>4. Response Time</h2>
    <p>Our team will review support requests and respond as soon as possible.</p>

    <h2>5. Privacy Policy</h2>
    <p>
      Learn how we handle your information in our{" "}
      <Link to="/privacy-policy">Privacy Policy</Link>.
    </p>

      <section className="contactus-sec contact-support-form">
        <h2>Send Us a Message</h2>
        <p>
          You can also contact our support team using the form below.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-4 col-md-4">
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={userNameLimit}
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-md-4">
              <div className="form-group">
                <input
                  type="text"
                  name="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  maxLength={emailLimit}
                />
                {errors.email && (
                  <div className="invalid-feedback">{errors.email}</div>
                )}
              </div>
            </div>
            <div className="col-lg-4 col-md-4">
              <div className="form-group">
                <input
                  type="text"
                  name="phone"
                  className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                  placeholder="Phone Number (optional)"
                  value={formData.phone}
                  onChange={(event) => {
                    const { value } = event.target;

                    if (/^(\+?[0-9\s\-()]*)$/.test(value)) {
                      if (value.includes("+") && value.indexOf("+") !== 0) {
                        return;
                      }
                      handleChange(event);
                    }
                  }}
                  inputMode="tel"
                />
                {errors.phone && (
                  <div className="invalid-feedback">{errors.phone}</div>
                )}
              </div>
            </div>
            <div className="col-lg-12 col-md-12">
              <div className="form-group">
                <textarea
                  name="message"
                  className={`form-control ${errors.message ? "is-invalid" : ""}`}
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                />
                {errors.message && (
                  <div className="invalid-feedback">{errors.message}</div>
                )}
              </div>
            </div>
            <div className="col-lg-12 col-md-12">
              <button className="leave-btn" type="submit" disabled={loading}>
                {loading ? selectLoader(25) : "Leave us a Message"}
              </button>
              {responseMessage && (
                <p className="response-message" role="alert">
                  {responseMessage}
                </p>
              )}
            </div>
          </div>
        </form>
      </section>
    </LegalPage>
  );
};

export default Contactus;
